import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  ArrowBack,
  Close,
  PersonAdd,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { useRegisterEmail } from "../hooks/auth";

export default function Register() {
  const [registerEmail, loadingRegister, errorRegister, clearErrorRegister] =
    useRegisterEmail();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordButton, setShowPasswordButton] = useState(false);

  const handleShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) =>
    e.preventDefault();

  const handleRegisterAccount = async () => {
    setShowPassword(false);
    registerEmail(email, password, firstName, lastName);
  };

  return (
    <Container maxWidth="xs">
      <Box ml={-1} mt={1}>
        <IconButton
          component={RouterLink}
          to="/login"
          aria-label="delete"
          size="large"
        >
          <ArrowBack />
        </IconButton>
      </Box>

      <Box
        mt={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <PersonAdd fontSize="large" color="disabled" />
        <Typography variant="h6" component="h1" ml={2}>
          Registrera konto
        </Typography>
      </Box>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel
          htmlFor="firstName"
          error={!!errorRegister?.firstName || !!errorRegister?.other}
        >
          Förnamn
        </InputLabel>
        <OutlinedInput
          id="firstName"
          label="Förnamn"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={loadingRegister}
          error={!!errorRegister?.firstName || !!errorRegister?.other}
        />
        {!!errorRegister?.firstName && (
          <FormHelperText error>{errorRegister.firstName}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel
          htmlFor="lastName"
          error={!!errorRegister?.lastName || !!errorRegister?.other}
        >
          Efternamn
        </InputLabel>
        <OutlinedInput
          id="lastName"
          label="Efternamn"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={loadingRegister}
          error={!!errorRegister?.lastName || !!errorRegister?.other}
        />
        {!!errorRegister?.lastName && (
          <FormHelperText error>{errorRegister.lastName}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel htmlFor="email" error={!!errorRegister?.email || !!errorRegister?.other}>
          E-post
        </InputLabel>
        <OutlinedInput
          id="email"
          label="E-post"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loadingRegister}
          error={!!errorRegister?.email || !!errorRegister?.other}
        />
        {!!errorRegister?.email && (
          <FormHelperText error>{errorRegister.email}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel
          htmlFor="password"
          error={!!errorRegister?.password || !!errorRegister?.other}
        >
          Lösenord
        </InputLabel>
        <OutlinedInput
          id="password"
          label="Lösenord"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onFocus={() => setShowPasswordButton(true)}
          onBlur={() => setShowPasswordButton(false)}
          onChange={(e) => setPassword(e.target.value)}
          onKeyUp={(e) => e.key === 'Enter' && handleRegisterAccount()}
          disabled={loadingRegister}
          error={!!errorRegister?.password || !!errorRegister?.other}
          endAdornment={
            showPasswordButton && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPassword}
                  onMouseDown={handleMouseDown}
                  disabled={loadingRegister}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }
        />
        {!!errorRegister?.password && (
          <FormHelperText error>{errorRegister.password}</FormHelperText>
        )}
      </FormControl>

      <Box mt={2}>
        <LoadingButton
          fullWidth
          size="large"
          loading={loadingRegister}
          variant="contained"
          disabled={loadingRegister}
          onClick={handleRegisterAccount}
        >
          Registrera
        </LoadingButton>
      </Box>

      <Snackbar
        open={!!errorRegister}
        autoHideDuration={6000}
        onClose={clearErrorRegister}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={clearErrorRegister}
            >
              <Close />
            </IconButton>
          }
        >
          {errorRegister?.firstName}
          {errorRegister?.lastName}
          {errorRegister?.email}
          {errorRegister?.password}
          {errorRegister?.other}
        </Alert>
      </Snackbar>
    </Container>
  )
}
